import React, { useEffect, useState } from 'react';
import PageMeta from '../components/PageMeta';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Target, Eye, Award, Users, Sparkles, Heart, Rocket, Shield, TrendingUp, Clock, CheckCircle } from 'lucide-react';
import { mockData } from '../mock';
import MobileCardSlider from '../components/MobileCardSlider';
import useCounterAnimation from '../hooks/useCounterAnimation';

const About = () => {
  const [activeValue, setActiveValue] = useState(0);
  
  // Counter animations for stats
  const [yearsCount, yearsRef] = useCounterAnimation(15, 1500, 200);
  const [expertsCount, expertsRef] = useCounterAnimation(50, 1500, 200);
  const [clientsCount, clientsRef] = useCounterAnimation(10000, 2000, 200);
  
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Auto-rotate values every 3 seconds
    const interval = setInterval(() => {
      setActiveValue((prev) => (prev + 1) % 4);
    }, 3000);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen page-transition">
      <PageMeta
        title="About Us"
        description="Learn about Global Trademark Federation - a trusted team of trademark professionals dedicated to protecting your brand identity across all 50 US states."
      />
      {/* Hero Section - Enhanced Creative */}
      <section
        className="relative bg-cover bg-center pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(45, 48, 71, 0.85), rgba(45, 48, 71, 0.70)), url(${mockData.heroImages.about})`,
        }}
      >
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0aDh2LThoLTh2OHptLTE2IDBoOHYtOGgtOHY4em0tMTYgMGg4di04aC04djh6bS0xNiAwaDh2LThoLTh2OHptLTE2IDBoOHYtOGgtOHY4eiIvPjwvZz48L2c+PC9zdmc+')] opacity-10" />
        
        {/* Floating shapes */}
        <div className="absolute top-20 right-10 w-32 h-32 bg-secondary/20 rounded-full blur-2xl animate-float" />
        <div className="absolute bottom-20 left-10 w-24 h-24 bg-accent/20 rounded-full blur-xl animate-float" style={{ animationDelay: '1s' }} />
        
        <div className="container mx-auto px-4 text-center text-white relative z-10">
          <div className="inline-block mb-6 animate-bounce">
            <span className="bg-gradient-to-r from-secondary/30 to-white/20 backdrop-blur-md text-white px-6 py-3 rounded-full text-sm font-bold border-2 border-white/40 shadow-xl">
              <Sparkles className="w-4 h-4 inline mr-2" />
              Our Story
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 animate-fade-in">About Us</h1>
          <p className="text-lg md:text-xl max-w-3xl mx-auto opacity-95 leading-relaxed animate-fade-in" style={{ animationDelay: '0.2s' }}>
            Your trusted partner in protecting intellectual property across the United States
          </p>
          
          {/* Stats Bar */}
          <div className="mt-12 grid grid-cols-3 gap-6 max-w-3xl mx-auto">
            <div ref={yearsRef} className="bg-white/10 backdrop-blur-lg rounded-xl p-4 border border-white/20 hover:bg-white/20 transition-all animate-float">
              <div className="text-3xl font-bold text-secondary">{yearsCount}+</div>
              <div className="text-sm opacity-90">Years Experience</div>
            </div>
            <div ref={expertsRef} className="bg-white/10 backdrop-blur-lg rounded-xl p-4 border border-white/20 hover:bg-white/20 transition-all animate-float" style={{ animationDelay: '0.5s' }}>
              <div className="text-3xl font-bold text-secondary">{expertsCount}+</div>
              <div className="text-sm opacity-90">Legal Experts</div>
            </div>
            <div ref={clientsRef} className="bg-white/10 backdrop-blur-lg rounded-xl p-4 border border-white/20 hover:bg-white/20 transition-all animate-float" style={{ animationDelay: '1s' }}>
              <div className="text-3xl font-bold text-secondary">{(clientsCount / 1000).toFixed(0)}K+</div>
              <div className="text-sm opacity-90">Happy Clients</div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Story - Enhanced Creative Layout */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute top-20 right-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl opacity-50 animate-pulse" />
        <div className="absolute bottom-20 left-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl opacity-50 animate-pulse" style={{ animationDelay: '1s' }} />
        
        <div className="container mx-auto px-4 relative">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-block mb-4">
                <span className="bg-secondary text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg">
                  Who We Are
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4">Building Trust Through Excellence</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Dedicated to protecting what matters most to your business
              </p>
            </div>
            
            {/* Grid Layout with visual appeal */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-gradient-to-br from-primary/5 via-white to-accent/5 rounded-3xl p-10 border-2 border-primary/20 hover:border-accent transition-all hover:shadow-2xl hover:-translate-y-2 duration-300 group">
                <div className="bg-gradient-to-br from-secondary/10 to-accent/20 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all">
                  <Rocket className="w-8 h-8 text-secondary" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-secondary transition-colors">Our Mission</h3>
                <p className="text-gray-700 leading-relaxed">
                  To make trademark registration accessible, affordable, and stress-free for businesses of all sizes. We provide the expertise and support you need at every step.
                </p>
              </div>
              
              <div className="bg-gradient-to-br from-accent/5 via-white to-secondary/5 rounded-3xl p-10 border-2 border-accent/20 hover:border-secondary transition-all hover:shadow-2xl hover:-translate-y-2 duration-300 group">
                <div className="bg-gradient-to-br from-accent/20 to-secondary/10 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all">
                  <Target className="w-8 h-8 text-secondary" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-secondary transition-colors">Our Vision</h3>
                <p className="text-gray-700 leading-relaxed">
                  To be the most trusted trademark services provider in the United States, empowering businesses to build and protect their brand legacy with confidence.
                </p>
              </div>
            </div>
            
            {/* Single column highlight */}
            <div className="bg-gradient-to-r from-secondary/10 via-primary/5 to-accent/10 rounded-3xl p-12 border-2 border-secondary/30 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-secondary/10 rounded-full blur-3xl" />
              <div className="relative z-10 flex items-start gap-6">
                <div className="bg-secondary/20 w-20 h-20 rounded-3xl flex items-center justify-center flex-shrink-0">
                  <Shield className="w-10 h-10 text-secondary" />
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Why Choose Us?</h3>
                  <p className="text-lg text-gray-700 leading-relaxed mb-6">
                    At <span className="font-bold text-secondary">Global Trademark Federation</span>, we understand that your brand is more than just a name or logo - it's your reputation, identity, and years of hard work. With extensive experience in trademark law and intellectual property protection, we've become a trusted name in the industry.
                  </p>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0" />
                      <span className="text-sm font-semibold text-gray-700">Expert Legal Team</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0" />
                      <span className="text-sm font-semibold text-gray-700">Fast Processing</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0" />
                      <span className="text-sm font-semibold text-gray-700">Affordable Pricing</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values - Interactive Grid */}
      <section className="py-20 bg-gradient-to-br from-gray-50 via-primary/5 to-accent/5 relative">
        <div className="absolute inset-0 bg-grid-gray-900/[0.02] bg-[size:60px_60px]" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-secondary text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg">
                Our Core Values
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4">What Drives Us Forward</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          {/* Desktop View - Grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {[
              { icon: Target, title: 'Excellence', desc: 'We strive for excellence in every aspect of our service delivery and client relationships.', color: 'primary', delay: '0s' },
              { icon: Eye, title: 'Transparency', desc: 'Clear communication and honest pricing with no hidden fees or surprises.', color: 'accent', delay: '0.1s' },
              { icon: Award, title: 'Expertise', desc: 'Deep knowledge of trademark law and intellectual property protection.', color: 'secondary', delay: '0.2s' },
              { icon: Users, title: 'Client-Focused', desc: 'Your success is our success. We\'re dedicated to your brand protection goals.', color: 'primary', delay: '0.3s' }
            ].map((value, index) => {
              const Icon = value.icon;
              const isActive = activeValue === index;
              
              return (
                <Card 
                  key={index}
                  onMouseEnter={() => setActiveValue(index)}
                  className={`group hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border-2 cursor-pointer relative overflow-hidden ${
                    isActive ? 'border-secondary shadow-2xl -translate-y-2' : 'border-gray-200 hover:border-accent'
                  }`}
                  style={{ animationDelay: value.delay }}
                >
                  {/* Background gradient overlay */}
                  <div className={`absolute inset-0 bg-gradient-to-br from-secondary/5 to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                  
                  <CardContent className="p-8 text-center relative z-10">
                    <div className={`bg-gradient-to-br from-${value.color}/10 to-accent/20 w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-6 transition-all duration-500 ${
                      isActive ? 'scale-110 rotate-6 shadow-lg' : 'group-hover:scale-110 group-hover:rotate-3'
                    }`}>
                      <Icon className={`w-10 h-10 text-secondary transition-all ${isActive ? 'animate-pulse' : ''}`} />
                    </div>
                    <h3 className={`text-xl font-bold mb-3 transition-colors duration-300 ${
                      isActive ? 'text-secondary' : 'text-gray-900 group-hover:text-secondary'
                    }`}>
                      {value.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-sm">
                      {value.desc}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Mobile View - Slider */}
          <div className="md:hidden px-4">
            <MobileCardSlider showDots={true} autoScroll={true}>
              {[
                { icon: Target, title: 'Excellence', desc: 'We strive for excellence in every aspect of our service delivery and client relationships.', color: 'primary' },
                { icon: Eye, title: 'Transparency', desc: 'Clear communication and honest pricing with no hidden fees or surprises.', color: 'accent' },
                { icon: Award, title: 'Expertise', desc: 'Deep knowledge of trademark law and intellectual property protection.', color: 'secondary' },
                { icon: Users, title: 'Client-Focused', desc: 'Your success is our success. We\'re dedicated to your brand protection goals.', color: 'primary' }
              ].map((value, index) => {
                const Icon = value.icon;
                
                return (
                  <Card 
                    key={index}
                    className="border-2 border-gray-200 hover:border-accent cursor-pointer relative overflow-hidden shadow-lg"
                  >
                    {/* Background gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-accent/5" />
                    
                    <CardContent className="p-6 text-center relative z-10">
                      <div className={`bg-gradient-to-br from-${value.color}/10 to-accent/20 w-16 h-16 rounded-3xl flex items-center justify-center mx-auto mb-4`}>
                        <Icon className="w-8 h-8 text-secondary" />
                      </div>
                      <h3 className="text-lg font-bold mb-3 text-gray-900">
                        {value.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed text-sm">
                        {value.desc}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </MobileCardSlider>
          </div>
          
          {/* Progress indicator */}
          <div className="flex justify-center gap-2 mt-12">
            {[0, 1, 2, 3].map((index) => (
              <button
                key={index}
                onClick={() => setActiveValue(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  activeValue === index ? 'bg-secondary w-8' : 'bg-gray-300 hover:bg-accent'
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach - Timeline Style */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute top-20 left-1/2 w-1 h-full bg-gradient-to-b from-primary/20 via-primary/10 to-primary/20 transform -translate-x-1/2 hidden lg:block" />
        
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-block mb-4">
                <span className="bg-gradient-to-r from-primary to-secondary text-white px-4 py-2 rounded-full text-sm font-semibold">
                  Our Approach
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-gray-900 via-primary to-gray-900 bg-clip-text text-transparent mb-4">How We Work</h2>
            </div>
            
            <div className="space-y-12">
              <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center">
                <div className="lg:flex-1 lg:text-right">
                  <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border-2 border-primary/10 hover:border-secondary transition-all hover:shadow-lg">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Personalized Service</h3>
                    <p className="text-gray-700 leading-relaxed">
                      We don't believe in one-size-fits-all solutions. Every business is unique, and we tailor our services to meet your specific needs.
                    </p>
                  </div>
                </div>
                <div className="hidden lg:flex w-12 h-12 rounded-full bg-gradient-to-br from-primary to-primary/80 flex-shrink-0 items-center justify-center text-white font-bold shadow-lg z-10">
                  1
                </div>
                <div className="lg:flex-1"></div>
              </div>

              <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center">
                <div className="lg:flex-1"></div>
                <div className="hidden lg:flex w-12 h-12 rounded-full bg-gradient-to-br from-primary to-primary/80 flex-shrink-0 items-center justify-center text-white font-bold shadow-lg z-10">
                  2
                </div>
                <div className="lg:flex-1">
                  <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border-2 border-primary/10 hover:border-secondary transition-all hover:shadow-lg">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Proactive Protection</h3>
                    <p className="text-gray-700 leading-relaxed">
                      We don't just register trademarks - we help you build a comprehensive brand protection strategy that grows with your business.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center">
                <div className="lg:flex-1 lg:text-right">
                  <div className="bg-gradient-to-br from-secondary/5 to-white rounded-2xl p-6 border-2 border-secondary/20 hover:border-secondary transition-all hover:shadow-lg">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Ongoing Support</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Our relationship doesn't end after registration. We provide continuous monitoring and support to ensure your brand remains protected.
                    </p>
                  </div>
                </div>
                <div className="hidden lg:flex w-12 h-12 rounded-full bg-gradient-to-br from-secondary to-secondary/80 flex-shrink-0 items-center justify-center text-white font-bold shadow-lg z-10">
                  3
                </div>
                <div className="lg:flex-1"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Creative */}
      <section className="py-20 bg-gradient-to-br from-primary via-primary/90 to-primary/80 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0aDh2LThoLTh2OHptLTE2IDBoOHYtOGgtOHY4em0tMTYgMGg4di04aC04djh6bS0xNiAwaDh2LThoLTh2OHptLTE2IDBoOHYtOGgtOHY4eiIvPjwvZz48L2c+PC9zdmc+')] opacity-20" />
        <div className="container mx-auto px-4 relative">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto text-center">
            <div className="group">
              <div className="text-5xl font-bold mb-2 group-hover:scale-110 transition-transform">10K+</div>
              <div className="text-lg opacity-90">Trademarks Registered</div>
            </div>
            <div className="group">
              <div className="text-5xl font-bold mb-2 group-hover:scale-110 transition-transform">98%</div>
              <div className="text-lg opacity-90">Success Rate</div>
            </div>
            <div className="group">
              <div className="text-5xl font-bold mb-2 group-hover:scale-110 transition-transform">50+</div>
              <div className="text-lg opacity-90">States Covered</div>
            </div>
            <div className="group">
              <div className="text-5xl font-bold mb-2 group-hover:scale-110 transition-transform">24/7</div>
              <div className="text-lg opacity-90">Client Support</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;